// Chat utility functions for parsing and processing responses

export interface StructuredResponse {
  action: 'update_itinerary';
  itinerary: {
    destination: string;
    dates: string;
    activities: Array<{
      id: number;
      day: string;
      activities: Array<{
        time: string;
        activity: string;
      }>;
    }>;
  };
  conversationSummary: string;
  message: string;
}

/**
 * Parses streaming content to extract structured JSON responses for itinerary updates
 * 
 * @param content - The raw streaming content that may contain JSON blocks
 * @returns Object with extracted StructuredResponse and cleaned text content
 */
export const parseStructuredResponse = (content: string): { response: StructuredResponse | null; cleanedContent: string } => {
  try {
    // Look for JSON blocks in the content (usually wrapped in triple backticks or just raw JSON)
    const patterns = [
      /```json\s*(\{[\s\S]*?\})\s*```/g,  // ```json { ... } ```
      /```\s*(\{[\s\S]*?\})\s*```/g,      // ``` { ... } ```
    ];

    let jsonData = null;
    let cleanedContent = content;
    
    // Try each pattern to find JSON
    for (let i = 0; i < patterns.length; i++) {
      const pattern = patterns[i];
      pattern.lastIndex = 0; // Reset regex state
      
      let matches: RegExpMatchArray[] = [];
      let match;
      while ((match = pattern.exec(content)) !== null) {
        matches.push(match);
      }
      
      for (const match of matches) {
        const jsonString = match[1];
        
        try {
          jsonData = JSON.parse(jsonString);
          
          // Validate it's a structured response
          if (jsonData.action === 'update_itinerary' && jsonData.itinerary) {
            // Remove the JSON block from the content to clean it up
            cleanedContent = content.replace(match[0], '').replace(/\n\s*\n+/g, '\n\n').trim();
            
            return { response: jsonData as StructuredResponse, cleanedContent };
          }
        } catch (parseError) {
          continue;
        }
      }
    }
    
    // Try to find raw JSON with update_itinerary action using a more balanced approach
    const rawJsonPattern = /(\{[^{]*"action"\s*:\s*"update_itinerary"[^}]*"itinerary"\s*:\s*\{[\s\S]*?\}\s*[^}]*\})/g;
    rawJsonPattern.lastIndex = 0;
    
    let match;
    while ((match = rawJsonPattern.exec(content)) !== null) {
      try {
        // Try to find the complete JSON by counting braces
        let braceCount = 0;
        let startPos = match.index;
        let endPos = startPos;
        
        for (let i = startPos; i < content.length; i++) {
          if (content[i] === '{') braceCount++;
          if (content[i] === '}') braceCount--;
          if (braceCount === 0) {
            endPos = i + 1;
            break;
          }
        }
        
        const jsonString = content.substring(startPos, endPos);
        jsonData = JSON.parse(jsonString);
        
        // Validate it's a structured response
        if (jsonData.action === 'update_itinerary' && jsonData.itinerary) {
          // Remove the JSON block from the content to clean it up
          cleanedContent = content.replace(jsonString, '').replace(/\n\s*\n+/g, '\n\n').trim();
          
          return { response: jsonData as StructuredResponse, cleanedContent };
        }
      } catch (parseError) {
        continue;
      }
    }
    
    return { response: null, cleanedContent };
    
  } catch (error) {
    console.error('Error parsing structured response:', error);
    return { response: null, cleanedContent: content };
  }
};